/**
 * What the linking becomes at scale: a knowledge graph of everything spoken,
 * with one golden thread pulled through it — lecture to reading to essay.
 *
 * The constellation above shows the mechanism on seven notes; this shows a
 * semester. Clusters of small dots (school in the teal, readings in the gold,
 * everything else in the neutral) joined by quiet fibers, and the thread of
 * three labeled hubs a reader can follow. "Second brain" is the one piece of
 * community vocabulary that has crossed into mainstream marketing, so the
 * section can say it plainly.
 *
 * Honesty boundary: the app has no graph screen. This is an illustration of
 * the vault's real link structure, and the ownership strip below closes the
 * loop — open the folder in Obsidian and its graph view draws this for real.
 * Nothing here claims an in-app surface.
 *
 * Raw DOM `<svg>` per `components/icons/glyph.tsx`; every colour is an audited
 * theme role, labels sit in `onSurfaceVariant` like every other secondary line.
 */

import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { MarketingSection } from "@/components/marketing/section";
import { SectionHead } from "@/components/marketing/section-head";
import { SANS_STACK, type AppTheme, useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";
import { PROSE_MEASURE } from "@/theme/web-tokens";

const LEDE =
  "One spoken note is a reminder. A few hundred, linked, are a map of what you know: lectures joined to readings, readings to essays, essays to the idea you had on a Tuesday walk. You never filed a thing.";

const CAPTION =
  "Come exam week, start at the essay and walk the thread backwards. Every claim leads back to the lecture and the chapter it came from, all in your own words.";

const GRAPH_LABEL =
  "A knowledge graph of many small notes in clusters, with one golden thread of links running from a lecture note through a reading note to an essay draft.";

type Cluster = "school" | "reading" | "everyday";

interface Dot {
  x: number;
  y: number;
  r: number;
  cluster: Cluster;
}

const DOTS: readonly Dot[] = [
  { x: 150, y: 120, r: 7, cluster: "school" },
  { x: 205, y: 88, r: 6, cluster: "school" },
  { x: 190, y: 170, r: 6, cluster: "school" },
  { x: 120, y: 200, r: 5, cluster: "school" },
  { x: 232, y: 196, r: 6, cluster: "school" },
  { x: 560, y: 92, r: 6, cluster: "reading" },
  { x: 612, y: 130, r: 7, cluster: "reading" },
  { x: 666, y: 96, r: 5, cluster: "reading" },
  { x: 700, y: 160, r: 6, cluster: "reading" },
  { x: 648, y: 190, r: 5, cluster: "reading" },
  { x: 330, y: 300, r: 6, cluster: "everyday" },
  { x: 462, y: 296, r: 6, cluster: "everyday" },
  { x: 520, y: 330, r: 5, cluster: "everyday" },
  { x: 584, y: 300, r: 6, cluster: "everyday" },
  { x: 282, y: 262, r: 5, cluster: "everyday" },
] as const;

const FIBERS: readonly string[] = [
  "M150 120 L205 88",
  "M150 120 L190 170",
  "M205 88 L268 122",
  "M190 170 L268 122",
  "M268 122 L232 196",
  "M120 200 L190 170",
  "M120 200 L150 120",
  "M560 92 L612 130",
  "M612 130 L666 96",
  "M560 92 L520 148",
  "M666 96 L700 160",
  "M612 130 L648 190",
  "M330 300 L396 330",
  "M396 330 L462 296",
  "M462 296 L520 330",
  "M330 300 L282 262",
  "M520 330 L584 300",
  "M232 196 L282 262",
  "M648 190 L584 300",
  "M520 148 L462 296",
] as const;

/** Lecture → reading → essay, the thread the caption walks backwards. */
const THREAD: readonly string[] = [
  "M268 122 Q 390 96 520 148",
  "M520 148 Q 480 230 396 330",
] as const;

const HUBS = [
  { x: 268, y: 122, label: "Lecture" },
  { x: 520, y: 148, label: "Reading" },
  { x: 396, y: 330, label: "Essay draft" },
] as const;

function clusterColor(cluster: Cluster, theme: AppTheme): string {
  switch (cluster) {
    case "school":
      return theme.colors.primary;
    case "reading":
      return theme.colors.tertiary;
    case "everyday":
      return theme.colors.outline;
  }
}

function BrainGraph({ theme }: { theme: AppTheme }) {
  return (
    <svg
      viewBox="0 0 820 380"
      role="img"
      aria-label={GRAPH_LABEL}
      style={{
        display: "block",
        width: "100%",
        maxWidth: 820,
        height: "auto",
      }}
    >
      {FIBERS.map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={theme.colors.outline}
          strokeWidth={1}
          opacity={0.4}
        />
      ))}
      {THREAD.map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={theme.colors.tertiary}
          strokeWidth={2}
        />
      ))}

      {DOTS.map(({ x, y, r, cluster }) => (
        <circle
          key={`${x}-${y}`}
          cx={x}
          cy={y}
          r={r}
          fill={clusterColor(cluster, theme)}
          opacity={cluster === "everyday" ? 0.7 : 0.85}
        />
      ))}

      {HUBS.map(({ x, y, label }) => (
        <g key={label}>
          <circle
            cx={x}
            cy={y}
            r={10}
            fill={theme.colors.tertiaryContainer}
            stroke={theme.colors.tertiary}
            strokeWidth={1.5}
          />
          <text
            x={x}
            y={y + 30}
            textAnchor="middle"
            fill={theme.colors.onSurfaceVariant}
            fontSize={11.5}
            letterSpacing={1}
            fontFamily={SANS_STACK}
            style={{ textTransform: "uppercase" }}
          >
            {label}
          </text>
        </g>
      ))}
    </svg>
  );
}

export function SecondBrain() {
  const theme = useAppTheme();

  return (
    <MarketingSection style={styles.section}>
      <SectionHead
        eyebrow="Second brain"
        heading="A semester of thoughts, still connected."
        lede={LEDE}
      />

      <View style={styles.graph}>
        <BrainGraph theme={theme} />
      </View>

      <Text
        variant="bodyLarge"
        style={[styles.caption, { color: theme.colors.onSurfaceVariant }]}
      >
        {CAPTION}
      </Text>
    </MarketingSection>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: spacing.xl,
  },
  graph: {
    alignItems: "center",
    width: "100%",
  },
  caption: {
    maxWidth: PROSE_MEASURE,
    alignSelf: "center",
    width: "100%",
    textAlign: "center",
  },
});
